import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import Avatar from '@mui/material/Avatar';
import { useAuth } from './AuthContext';
import api from '../api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function StaffLogin() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Email/password login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Email link login state
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);

  // Password change state
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Phone 2FA state
  const [phone2FAOpen, setPhone2FAOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signIn({ email, password });
      navigate('/', { replace: true });
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Login failed. Please check your credentials.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLinkLogin = async (e) => {
    e.preventDefault();
    if (!linkEmail) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/logins/email-link', { email: linkEmail });
      setLinkSent(true);
      setSuccess('Login link sent! Check your email.');
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to send login link.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setError('Please fill in all password fields.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/logins/change-password', {
        currentPassword,
        newPassword,
      });
      setSuccess('Password changed successfully.');
      setChangePasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to change password.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerificationCode = async () => {
    if (!phoneNumber) {
      setError('Please enter your phone number.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/logins/2fa/setup', { phoneNumber });
      setCodeSent(true);
      setSuccess('Verification code sent to your phone.');
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to send verification code.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError('Please enter the verification code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/logins/2fa/verify', { phoneNumber, code: verificationCode });
      setSuccess('Phone number verified. 2FA is now enabled.');
      setPhone2FAOpen(false);
      setPhoneNumber('');
      setVerificationCode('');
      setCodeSent(false);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Invalid verification code.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.dark', width: 56, height: 56 }}>
          <AdminPanelSettingsOutlinedIcon fontSize="large" />
        </Avatar>
        <Typography component="h1" variant="h5">
          Staff Login
        </Typography>

        <Paper sx={{ mt: 3, width: '100%' }} elevation={2}>
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Email & Password" />
            <Tab label="Email Link" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Email & Password Tab */}
            <TabPanel value={tab} index={0}>
              <Box component="form" onSubmit={handleEmailPasswordLogin} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="staff-email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="staff-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Box>

              <Divider sx={{ my: 2 }}>Account Options</Divider>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setChangePasswordOpen(true)}
                >
                  Change Password
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhoneAndroidOutlinedIcon />}
                  onClick={() => setPhone2FAOpen(true)}
                >
                  Setup 2FA
                </Button>
              </Box>
            </TabPanel>

            {/* Email Link Tab */}
            <TabPanel value={tab} index={1}>
              <Box component="form" onSubmit={handleEmailLinkLogin} noValidate>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your email address and we'll send you a sign-in link.
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="link-email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  disabled={loading || linkSent}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || linkSent}
                  startIcon={linkSent ? <EmailOutlinedIcon /> : loading ? <CircularProgress size={20} color="inherit" /> : <EmailOutlinedIcon />}
                >
                  {linkSent ? 'Link Sent - Check Your Email' : loading ? 'Sending...' : 'Send Sign-In Link'}
                </Button>
                {linkSent && (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => { setLinkSent(false); setLinkEmail(''); }}
                  >
                    Send to a different email
                  </Button>
                )}
              </Box>
            </TabPanel>
          </Box>
        </Paper>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Current Password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="New Password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            helperText="Minimum 8 characters"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Confirm New Password"
            type="password"
            autoComplete="new-password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            disabled={loading}
            error={confirmNewPassword !== '' && newPassword !== confirmNewPassword}
            helperText={confirmNewPassword !== '' && newPassword !== confirmNewPassword ? 'Passwords do not match' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phone 2FA Setup Dialog */}
      <Dialog open={phone2FAOpen} onClose={() => setPhone2FAOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add your phone number for two-factor authentication. You will receive a verification code via SMS.
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Phone Number"
            placeholder="+1234567890"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={loading || codeSent}
            helperText="Include country code (e.g., +1 for US)"
          />
          {!codeSent ? (
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleSendVerificationCode}
              disabled={loading || !phoneNumber}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </Button>
          ) : (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Verification Code"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={loading}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleVerifyCode}
                disabled={loading || !verificationCode}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <Button
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
                onClick={() => { setCodeSent(false); setVerificationCode(''); }}
                disabled={loading}
              >
                Resend Code
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPhone2FAOpen(false); setCodeSent(false); setVerificationCode(''); }} disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" variant="filled" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
}
